import { Container, Text, type Texture } from 'pixi.js';
import { BoardPanel } from '../board/BoardPanel';
import { animate, COLOURS, displayStyle, ease, uiStyle } from '../board/ui';

/** What the first-visit hint says. */
export const SPIN_HINT_TITLE = 'Tap SPIN to play';
export const SPIN_HINT_LINE = 'Coins are free, and we top you up whenever you run out.';

/** The hint as plain data, for browser tests. */
export interface SpinHintSnapshot {
  visible: boolean;
  lines: string[];
}

/**
 * A new player's first look at a slot: what to tap, and the free-coins
 * promise, on a small board (the slot's reel floor with a brass edge) in
 * the game's column of notices under the top bar. It takes no taps; the
 * host takes it away on the first spin or after a while. Drawn on the
 * slot's canvas.
 */
export class SpinHint extends Container {
  private readonly board = new BoardPanel({ radius: 14 });
  private readonly title: Text;
  private readonly line: Text;
  private shown = false;

  constructor(private readonly floor: () => Texture | null) {
    super();
    this.title = new Text({ text: SPIN_HINT_TITLE, style: displayStyle(20) });
    this.title.anchor.set(0.5, 0);
    this.line = new Text({ text: SPIN_HINT_LINE, style: { ...uiStyle(13, COLOURS.cream, '500'), align: 'center', wordWrap: true, lineHeight: 17 } });
    this.line.anchor.set(0.5, 0);
    this.addChild(this.board, this.title, this.line);
    this.eventMode = 'none';
    this.visible = false;
    this.alpha = 0;
  }

  get snapshot(): SpinHintSnapshot {
    return { visible: this.visible && this.shown, lines: [this.title.text, this.line.text] };
  }

  /** Centre it on `centreX`, no wider than `maxWidth`. */
  layout(centreX: number, maxWidth: number): void {
    const width = Math.round(Math.min(320, maxWidth));
    this.line.style.wordWrapWidth = width - 32;
    this.board.setFloor(this.floor());
    this.title.position.set(width / 2, 10);
    this.line.position.set(width / 2, this.title.y + this.title.height + 2);
    this.board.resize(width, Math.round(this.line.y + this.line.height + 12));
    this.pivot.set(width / 2, 0);
    this.x = Math.round(centreX);
  }

  /** Its height, for its place in the notices' column. */
  get boxHeight(): number {
    return this.board.boardHeight;
  }

  /** Its top edge, from the notices' column. */
  setTop(y: number): void {
    this.y = Math.round(y);
  }

  /** The column may hide it for a while (the refill): it comes back after. */
  setAllowed(allowed: boolean): void {
    this.allowed = allowed;
    this.visible = (this.shown || this.alpha > 0) && allowed;
  }

  /** Its box on screen while it shows (browser tests). */
  get rect(): { x: number; y: number; width: number; height: number } | null {
    if (!this.visible || !this.shown) return null;
    const b = this.board.getBounds();
    return { x: b.x, y: b.y, width: b.width, height: b.height };
  }

  /** Bring it in or take it away; resolves when it has faded. */
  setShown(shown: boolean): Promise<void> {
    if (shown === this.shown) return Promise.resolve();
    this.shown = shown;
    if (shown) {
      this.board.setFloor(this.floor());
      this.visible = this.allowed;
      return animate(400, (t) => (this.alpha = ease.outCubic(t)));
    }
    return animate(200, (t) => (this.alpha = 1 - t)).then(() => {
      if (!this.destroyed && !this.shown) this.visible = false;
    });
  }

  private allowed = true;
}
